import { AiOutlineClose } from "react-icons/ai";
import {
  CancelBtn,
  Header,
  MediumContainer,
  NextBtn,
} from "../../../screens/User/Users/components/IncidentType/incident.style";
import { useIncidentFormContext } from "./IncidentContext";
import { useIncidentData } from "../../../layouts/incidentLayout";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { stages } from "../../../utils/helpers/incidentFormTabs";

interface ISaveOrDiscardPromptProps {
  saveForm: () => any;
  submitForm: () => any;
  canSaveForm: boolean;
  discardForm: (x?: string) => void;
  isCreateButtonDisabled: boolean;
  getSubmitButtonName: (color?: string, radius?: number) => any;
}

const SaveOrDiscardPrompt = ({
  isCreateButtonDisabled,
  canSaveForm,
  saveForm,
  submitForm,
  discardForm,
  getSubmitButtonName,
}: ISaveOrDiscardPromptProps) => {
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const location = useLocation();

  const { shouldStayOnPage, setShouldStayOnPage, exitLink } =
    useIncidentFormContext();

  const {
    shouldNavigateToApprovals,
    saveIncidentLoading,
    setIncidentFormState,
    setShouldNavigateToApprovals,
  } = useIncidentData();

  function navigateToApprovals() {
    setShouldNavigateToApprovals(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("mode", "view");
    const pathSegments = location.pathname.split("/");
    pathSegments.pop();
    const link = exitLink
      ? stages.find((stage) => stage.label === exitLink)?.path
      : "";
    link && pathSegments.push(link);
    const newPath = `${pathSegments.join("/")}?${newSearchParams.toString()}`;
    link && navigate(newPath);
  }

  async function handleSave() {
    try {
      const result = await saveForm();
      if (result) {
        setIncidentFormState({ showPrompt: false });
        if (shouldStayOnPage) {
          setShouldStayOnPage(false);
        } else {
          discardForm();
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  const handleSubmit = async () => {
    try {
      const result = await submitForm();
      if (result) {
        setIncidentFormState({ showPrompt: false });
        if (!shouldNavigateToApprovals && !shouldStayOnPage) {
          discardForm();
        } else if (shouldNavigateToApprovals && !shouldStayOnPage) {
          navigateToApprovals();
        } else if (shouldStayOnPage) {
          setShouldStayOnPage(false);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const _handleDiscard = () => {
    const pathSegments = location.pathname.split("/");
    pathSegments.pop();
    let nextPath;
    if (exitLink) {
      nextPath = stages.find((stage) => stage.label === exitLink)?.path;
    }
    if (nextPath) {
      const nextPathSegments = nextPath.split("/");
      const path = nextPathSegments.pop() ?? "";
      pathSegments.push(path);
      nextPath = `${pathSegments.join("/")}${"?mode=view"}`;
    }
    if (nextPath) {
      setIncidentFormState({ showPrompt: false, exitLink: "" });
      navigate(nextPath);
    } else {
      discardForm();
    }
  };

  const handleModalClose = () => {
    setIncidentFormState({ showPrompt: false });
  };

  function getModalHeader() {
    if (!canSaveForm) {
      return isCreateButtonDisabled
        ? "Discard unsubmitted changes?"
        : "Submit or Discard?";
    } else {
      return "Save or Discard?";
    }
  }

  function getModalBody() {
    if (!canSaveForm) {
      return isCreateButtonDisabled
        ? "You have unsaved changes. Would you like to return to the form or discard before exit?"
        : "You have unsaved changes. Would you like to submit or discard them before exit?";
    } else {
      return "You have unsaved changes. Would you like to save or discard them before exit?";
    }
  }

  return (
    <MediumContainer>
      <div className="flex">
        <div
          onClick={handleModalClose}
          style={{ fontSize: "30px", cursor: "pointer" }}
        >
          <AiOutlineClose size={25} />
        </div>
        <Header>{getModalHeader()}</Header>
      </div>
      <p className="text-md px-5 py-5 text-center">{getModalBody()}</p>

      <div className="mb-14 mr-7 mt-5">
        {canSaveForm ? (
          <NextBtn
            data-testid="save-draft-btn"
            onClick={handleSave}
            disabled={saveIncidentLoading}
          >
            Save
          </NextBtn>
        ) : (
          <NextBtn
            data-testid="submit-draft-btn"
            type="submit"
            onClick={handleSubmit}
            disabled={isCreateButtonDisabled}
          >
            {getSubmitButtonName("var(--primaryWhiteMain)", 20)}
          </NextBtn>
        )}
        <CancelBtn data-testid="discard-btn" onClick={_handleDiscard}>
          Discard
        </CancelBtn>
      </div>
    </MediumContainer>
  );
};

export default SaveOrDiscardPrompt;
